import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Profile {
  id: string;
  first_name: string;
  age: number;
  gender: string;
  location: string;
  education_level: string;
  religiosity_level: string;
  prayer_frequency: string;
  bio?: string;
  profile_picture_url?: string;
  profile_pictures?: string[] | null;
  marital_status: string;
  ethnicity: string;
  citizenship_status: string;
  height_feet?: number;
  height_inches?: number;
  sect: string;
  masjid_attendance: string;
  occupation?: string;
  field_of_study?: string;
  employment_status?: string;
  career_description?: string;
  financial_status: number;
  personality_type: number;
  personality_description?: string;
  importance_of_islam: number;
  quran_knowledge: string;
  islamic_knowledge_level: string;
  smoking: string;
  dietary_preferences?: string[] | null;
  exercise_routine?: string;
  has_children?: boolean;
  children_count?: number;
  has_beard?: string;
  wears_hijab?: boolean;
  wears_hijab_level?: string;
  willing_to_marry_divorced?: boolean;
  willing_to_marry_with_children?: boolean;
  willing_to_marry_convert?: boolean;
  willing_to_relocate?: boolean;
  seeking_age_min: number;
  seeking_age_max: number;
  seeking_location_preference?: string;
  seeking_description?: string;
  seeking_education?: string[] | null;
  seeking_ethnicity?: string[] | null;
  seeking_citizenship?: string[] | null;
  seeking_sect?: string[] | null;
  seeking_religiosity?: string[] | null;
  seeking_marital_status?: string[] | null;
  seeking_personality_min?: number;
  seeking_personality_max?: number;
  seeking_height_min_feet?: number;
  seeking_height_min_inches?: number;
  seeking_height_max_feet?: number;
  seeking_height_max_inches?: number;
  seeking_financial_min?: number;
  seeking_islam_importance_min?: number;
  seeking_masjid_attendance_min?: string;
  seeking_beard_preference?: string;
  seeking_hijab_preference?: string;
  seeking_children_ok?: boolean;
  seeking_divorced_ok?: boolean;
  seeking_convert_ok?: boolean;
  seeking_preferences_weights?: any;
}

interface ContactInfo {
  email?: string;
  phone?: string;
  whatsapp?: string;
  preferred_contact_method?: string;
  is_wali?: boolean;
  contact_name?: string;
}

interface Match {
  id: string;
  profile_1_id: string;
  profile_2_id: string;
  status: string;
  profile_1_response?: string;
  profile_2_response?: string;
  created_at: string;
  notes?: string;
  created_by_matchmaker_id?: string;
  profile_1?: Profile;
  profile_2?: Profile;
  contact_info_1?: ContactInfo;
  contact_info_2?: ContactInfo;
}

export function useMatch(matchId: string | undefined, userId: string | undefined) {
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMatch = async () => {
    if (!matchId || !userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch match with profile data
      const { data: matchData, error: matchError } = await supabase
        .from("matches")
        .select(`
          *,
          profile_1:profiles!matches_profile_1_id_fkey(*),
          profile_2:profiles!matches_profile_2_id_fkey(*)
        `)
        .eq("id", matchId)
        .maybeSingle();

      if (matchError) throw matchError;

      if (!matchData) {
        console.error("match.not_found");
        setError("Match not found");
        return;
      }

      if (!matchData.profile_1) {
        console.warn("match.profile_1_unavailable");
      }
      if (!matchData.profile_2) {
        console.warn("match.profile_2_unavailable");
      }

      // Verify user is part of this match
      if (matchData.profile_1_id !== userId && matchData.profile_2_id !== userId) {
        console.error("match.unauthorized_access");
        setError("You don't have access to this match");
        return;
      }

      // If both accepted, fetch contact info. The viewer's own row is read
      // directly; the other party's must go through effective_contact(), which
      // is the only path that applies the wali reveal — and which returns
      // nothing for the viewer's own profile.
      if (matchData.status === "both_accepted") {
        const isProfile1 = matchData.profile_1_id === userId;
        const otherProfileId = isProfile1 ? matchData.profile_2_id : matchData.profile_1_id;

        const [{ data: ownContact }, { data: otherContactRows }] = await Promise.all([
          supabase
            .from("contact_info")
            .select("email, phone, whatsapp, preferred_contact_method")
            .eq("profile_id", userId)
            .maybeSingle(),
          supabase.rpc("effective_contact", {
            _subject_profile_id: otherProfileId,
          }),
        ]);

        const otherContact = otherContactRows?.[0];

        setMatch({
          ...matchData,
          contact_info_1: (isProfile1 ? ownContact : otherContact) || undefined,
          contact_info_2: (isProfile1 ? otherContact : ownContact) || undefined,
        });
      } else {
        setMatch(matchData);
      }
    } catch (err: any) {
      console.error("match.fetch_failed");
      setError(err.message || "Failed to load match");
      toast.error("Failed to load match details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatch();
  }, [matchId, userId]);

  // Real-time subscription
  useEffect(() => {
    if (!matchId) return;

    const channel = supabase
      .channel(`match-${matchId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "matches",
          filter: `id=eq.${matchId}`,
        },
        () => {
          fetchMatch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [matchId]);

  return { match, loading, error, refetch: fetchMatch };
}
